if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.initMainMediaSwipe();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const thumbnailButton = mediaToSwitch.querySelector('button');

          thumbnailButton.addEventListener('click', (event) => {
            if (event.defaultPrevented || thumbnailButton.getAttribute('aria-current') === 'true') return;

            this.setActiveMedia(mediaToSwitch.dataset.target, false);
          });
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initMainMediaSwipe() {
        const slider = this.elements.viewer?.slider;
        if (!slider || slider.dataset.mainMediaSwipeInit === 'true') return;
        if (this.getMediaItems().length < 2) return;

        slider.dataset.mainMediaSwipeInit = 'true';

        let pointerId = null;
        let startX = 0;
        let startY = 0;
        let isDragging = false;

        const resetSwipe = () => {
          pointerId = null;
          isDragging = false;
          slider.classList.remove('is-main-media-swiping');
        };

        slider.addEventListener('pointerdown', (event) => {
          if (!event.isPrimary || event.button > 0) return;
          if (event.target.closest('video, model-viewer, iframe, deferred-media, product-model, input, select, textarea')) {
            return;
          }

          pointerId = event.pointerId;
          startX = event.clientX;
          startY = event.clientY;
          isDragging = false;
        });

        slider.addEventListener('pointermove', (event) => {
          if (pointerId !== event.pointerId) return;

          const deltaX = event.clientX - startX;
          const deltaY = event.clientY - startY;
          const absX = Math.abs(deltaX);
          const absY = Math.abs(deltaY);

          if (absY > 24 && absY > absX) {
            resetSwipe();
            return;
          }

          if (absX > 12 && absX > absY * 1.2) {
            isDragging = true;
            slider.classList.add('is-main-media-swiping');
          }
        });

        slider.addEventListener('pointerup', (event) => {
          if (pointerId !== event.pointerId) return;

          const deltaX = event.clientX - startX;
          const deltaY = event.clientY - startY;
          const absX = Math.abs(deltaX);
          const absY = Math.abs(deltaY);
          const shouldChangeMedia = isDragging && absX > 44 && absX > absY * 1.35;

          resetSwipe();

          if (!shouldChangeMedia) return;

          this.suppressMediaClick = true;
          this.changeActiveMediaByDirection(deltaX < 0 ? 1 : -1);

          window.setTimeout(() => {
            this.suppressMediaClick = false;
          }, 250);
        });

        slider.addEventListener('pointercancel', resetSwipe);
        slider.addEventListener('dragstart', (event) => event.preventDefault());
        slider.addEventListener(
          'click',
          (event) => {
            if (!this.suppressMediaClick) return;

            event.preventDefault();
            event.stopImmediatePropagation();
          },
          true
        );
      }

      getMediaItems() {
        return Array.from(this.elements.viewer?.querySelectorAll('[data-media-id]') || []);
      }

      changeActiveMediaByDirection(direction) {
        const mediaItems = this.getMediaItems();
        if (mediaItems.length < 2) return;

        let activeIndex = mediaItems.findIndex((item) => item.classList.contains('is-active'));
        if (activeIndex < 0) activeIndex = Math.max((this.elements.viewer.currentPage || 1) - 1, 0);

        const nextIndex = Math.min(Math.max(activeIndex + direction, 0), mediaItems.length - 1);
        const nextMedia = mediaItems[nextIndex];

        if (!nextMedia || nextIndex === activeIndex) return;

        this.setActiveMedia(nextMedia.dataset.mediaId, false);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
